import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { useIntl } from 'react-intl'

import styled from '@emotion/styled'
import { Button, LinearProgress } from '@mui/material'

import { H3 } from '../../../layouts/TextStyles'
import { coursePriceApi } from '../../../features/course/coursePrice'
import { CoursePriceDetailsState } from '../../../features/types'
import CoursePriceDialog from './components/coursePriceDialog/CoursePriceDialog'
import PriceTable from './components/coursePriceDialog/components/PriceTable'
import Sentinel from '../../../components/infiniteScroll/Sentinel'
import { usePaginatedList } from '../../../app/hooks/usePaginatedList'
import { ui } from '../../../utils/uiTokens'

const PricePage = () => {
    const { formatMessage } = useIntl()
    const [openPriceDialog, setOpenPriceDialog] = useState<boolean>(false)
    const [currentRowData, setCurrentRowData] =
        useState<CoursePriceDetailsState>(null)

    const [fetchPrices] = coursePriceApi.useLazyFetchAllCoursesPriceQuery()

    const {
        rows,
        isLoading: isFetching,
        loadedPages,
        sentinelRef,
        reload,
    } = usePaginatedList((page, size) => fetchPrices({ page, size }).unwrap(), [])
    const [deleteCoursePrice, { isLoading: isDeleting }] =
        coursePriceApi.useDeleteCoursePriceMutation()
    const [activateCoursePrice, { isLoading: isActivating }] =
        coursePriceApi.useActivateCoursePriceMutation()

    const handleDelete = async (id: number) => {
        try {
            await deleteCoursePrice(id).unwrap()
            toast.success(formatMessage({ id: 'deletedSuccess' }))
            reload()
        } catch (e) {}
    }

    const handleActivate = async (id: number) => {
        try {
            await activateCoursePrice(id).unwrap()
            toast.success(formatMessage({ id: 'savedSuccess' }))
            reload()
        } catch (error) {}
    }

    const handleEditRow = (coursePrice: CoursePriceDetailsState) => {
        setCurrentRowData(coursePrice)
        setOpenPriceDialog(true)
    }

    useEffect(() => {
        if (!openPriceDialog) {
            setCurrentRowData(null)
        }
    }, [openPriceDialog])

    return (
        <Wrapper>
            <Content>
                <Header>
                    <Title>
                        <H3>{formatMessage({ id: 'coursesPrices' })}</H3>
                    </Title>

                    <Button
                        variant="contained"
                        onClick={() => setOpenPriceDialog(true)}
                    >
                        {formatMessage({ id: 'addCoursePrice'})}
                    </Button>
                </Header>

                {(isFetching && loadedPages === 0) || isDeleting || isActivating ? (
                    <LinearProgress />
                ) : (
                    <>
                        <PriceTable
                            prices={rows}
                            isFetching={isFetching}
                            handleOpen={handleEditRow}
                            onDelete={handleDelete}
                            onActivate={handleActivate}
                        />

                        <Sentinel ref={sentinelRef} isLoading={isFetching} />
                    </>
                )}
            </Content>

            <CoursePriceDialog
                open={openPriceDialog}
                editCoursePrice={currentRowData}
                handleClose={() => setOpenPriceDialog(false)}
            />
        </Wrapper>
    )
}

export default PricePage

const Wrapper = styled.div`
    margin: 10px;
    border-radius: 16px;
`
const Title = styled.div`
    text-align: left;
    display: flex;
    align-items: center;
`

const Content = styled.div`
    background-color: ${ui.card};
    border-radius: 16px;
    padding: 20px;
    min-height: 80vh;
`

const Header = styled.header`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;

    margin-bottom: 20px;
`
